import {
    OnboardingChatResultsTooltipType,
    RuleSetType,
    RuleType,
} from '@fruitful/types';
import React from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    LabelRegular,
    Shadows,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useConversationalMMContext } from '../context/ConversationalMMContextBase';
import useAdvanceTooltip from '../hooks/useAdvanceTooltip';
import type {
    RuleCompletionInfo,
    RuleSetDisplay,
} from '../utils/simulationDisplayUtils';
import { GuidedTooltip } from './GuidedTooltip';

const getRuleTooltipType = (
    rule: RuleCompletionInfo,
): OnboardingChatResultsTooltipType | null => {
    if (rule.type === RuleType.StarterEmergencyFund) {
        return OnboardingChatResultsTooltipType.StarterEmergencyFund;
    }
    if (rule.type === RuleType.DebtPayDown) {
        return OnboardingChatResultsTooltipType.DebtPayDown;
    }
    if (rule.type === RuleType.Savings) {
        return OnboardingChatResultsTooltipType.Savings;
    }
    return null;
};

const RuleRow = ({
    rule,
    showSplitPercentage = false,
    showBottomBorder,
    hideDates,
}: {
    rule: RuleCompletionInfo;
    showSplitPercentage?: boolean;
    showBottomBorder: boolean;
    hideDates?: boolean;
}) => {
    const { activeTooltip, tooltipSequence, tooltipData } =
        useConversationalMMContext();
    const advanceTooltip = useAdvanceTooltip();

    const splitLabel = showSplitPercentage
        ? ` (${Math.round(rule.splitPercentage * 100)}%)`
        : '';

    const dateLabel = hideDates
        ? null
        : (rule.completionLabel ?? rule.completionDate);

    const tooltipType = getRuleTooltipType(rule);
    const data = tooltipType != null ? tooltipData?.[tooltipType] : null;

    const ruleCard = (
        <XStack
            alignItems="center"
            gap={16}
            paddingHorizontal={16}
            paddingVertical={8}
            height={72}
            borderBottomWidth={showBottomBorder ? 1 : 0}
            borderBottomColor="$white"
        >
            <View
                width={24}
                height={24}
                alignItems="center"
                justifyContent="center"
            >
                <View
                    width={8}
                    height={8}
                    borderRadius={4}
                    backgroundColor="$petal"
                />
            </View>
            <YStack flex={1}>
                <LabelRegular color="$black">
                    {rule.description}
                    {splitLabel}
                </LabelRegular>
                {rule.targetDisplay !== '' && (
                    <BodyMediumEmphasized>
                        {rule.targetDisplay}
                    </BodyMediumEmphasized>
                )}
            </YStack>
            {dateLabel != null && (
                <View
                    paddingVertical={8}
                    paddingHorizontal={12}
                    borderRadius={16}
                    backgroundColor="$white"
                    flexShrink={0}
                >
                    <LabelRegular color="$gray1000">{dateLabel}</LabelRegular>
                </View>
            )}
        </XStack>
    );

    return tooltipType != null && data != null ? (
        <GuidedTooltip
            title={data.title}
            content={data.description}
            isActive={activeTooltip === tooltipType}
            onNext={advanceTooltip}
            isLast={tooltipSequence[tooltipSequence.length - 1] === tooltipType}
            placement="top"
        >
            {ruleCard}
        </GuidedTooltip>
    ) : (
        ruleCard
    );
};

const getRuleSetTooltipType = (
    ruleSet: RuleSetDisplay,
): OnboardingChatResultsTooltipType | null => {
    if (ruleSet.type === RuleSetType.FinancialHealth) {
        return OnboardingChatResultsTooltipType.FinancialHealthSet;
    }
    return null;
};

export const RuleSetCard = ({
    ruleSet,
    showSplitPercentage,
    hideDates,
    hideDatesForAllocationType,
}: {
    ruleSet: RuleSetDisplay;
    showSplitPercentage?: boolean;
    hideDates?: boolean;
    hideDatesForAllocationType?: string;
}) => {
    const { activeTooltip, tooltipSequence, tooltipData } =
        useConversationalMMContext();
    const advanceTooltip = useAdvanceTooltip();

    const showSplit = showSplitPercentage ?? ruleSet.rules.length > 1;

    const tooltipType = getRuleSetTooltipType(ruleSet);
    const data = tooltipType != null ? tooltipData?.[tooltipType] : null;

    const ruleSetCard = (
        <YStack
            width="100%"
            backgroundColor="rgba(255, 255, 255, 0.5)"
            borderRadius={16}
            borderWidth={1}
            borderColor="$white"
            {...Shadows.MEDIUM}
        >
            <XStack
                alignItems="center"
                gap={16}
                paddingHorizontal={16}
                paddingVertical={8}
                backgroundColor="rgba(255, 255, 255, 0.5)"
                borderBottomWidth={1}
                borderBottomColor="$white"
                overflow="hidden"
                borderTopLeftRadius={16}
                borderTopRightRadius={16}
            >
                <View
                    width={24}
                    height={24}
                    borderRadius={8}
                    backgroundColor="$white"
                    alignItems="center"
                    justifyContent="center"
                >
                    <BodyMediumEmphasized color="$petal">
                        {ruleSet.priority}
                    </BodyMediumEmphasized>
                </View>
                <BodyMediumRegular>{ruleSet.name}</BodyMediumRegular>
            </XStack>

            {ruleSet.rules.map((rule, index) => (
                <RuleRow
                    key={rule.description}
                    rule={rule}
                    showSplitPercentage={showSplit}
                    showBottomBorder={index < ruleSet.rules.length - 1}
                    hideDates={
                        hideDates === true ||
                        (hideDatesForAllocationType != null &&
                            rule.allocationType === hideDatesForAllocationType)
                    }
                />
            ))}
        </YStack>
    );

    return tooltipType != null && data != null ? (
        <GuidedTooltip
            title={data.title}
            content={data.description}
            isActive={activeTooltip === tooltipType}
            onNext={advanceTooltip}
            isLast={tooltipSequence[tooltipSequence.length - 1] === tooltipType}
            placement="top"
        >
            {ruleSetCard}
        </GuidedTooltip>
    ) : (
        ruleSetCard
    );
};
